import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';

/**
 * Hook: Fetch all cohorts for the current tenant
 */
export function useCohorts() {
  return useQuery({
    queryKey: ['cohorts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('analytics_cohorts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });
}

/**
 * Hook: Create a new cohort
 */
export function useCreateCohort() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      name: string;
      cohort_type:
        | 'signup_date'
        | 'company_size'
        | 'industry'
        | 'spending_tier'
        | 'activity_level'
        | 'custom';
      description?: string;
      filters: Record<string, any>;
    }) => {
      const response = await fetch('/functions/v1/analytics-cohort-builder/cohorts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        const err = (await response.json()) as { error?: { message?: string } };
        throw new Error(err.error?.message || 'Failed to create cohort');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cohorts'] });
    },
  });
}

/**
 * Hook: Refresh a cohort's membership
 */
export function useRefreshCohort() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cohortId: string) => {
      const response = await fetch(
        `/functions/v1/analytics-cohort-builder/cohorts/${cohortId}/refresh`,
        {
          method: 'POST',
        },
      );

      if (!response.ok) {
        const err = (await response.json()) as { error?: { message?: string } };
        throw new Error(err.error?.message || 'Failed to refresh cohort');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cohorts'] });
    },
  });
}

/**
 * Hook: List members of a cohort
 */
export function useCohortMembers(cohortId: string, limit = 50, offset = 0) {
  return useQuery({
    queryKey: ['cohort-members', cohortId, limit, offset],
    queryFn: async () => {
      const response = await fetch(
        `/functions/v1/analytics-cohort-builder/cohorts/${cohortId}/members?limit=${limit}&offset=${offset}`,
      );

      if (!response.ok) {
        throw new Error('Failed to fetch cohort members');
      }

      return response.json();
    },
    enabled: !!cohortId,
  });
}

/**
 * Hook: Compare metrics between two cohorts
 * (Implementation deferred; placeholder returns mock data)
 */
export function useCohortComparison(cohortId1: string, cohortId2: string) {
  return useQuery({
    queryKey: ['cohorts-compare', cohortId1, cohortId2],
    queryFn: async () => {
      // TODO: implement actual comparison logic
      return {
        cohort1_id: cohortId1,
        cohort2_id: cohortId2,
        churn_rate_diff: 0.0,
        avg_engagement_diff: 0.0,
        features_adopted_diff: 0,
      };
    },
    enabled: !!(cohortId1 && cohortId2),
  });
}

/**
 * Hook: Fetch funnel metrics (conversion %, time-in-stage, stuck entities)
 */
export function useFunnelMetrics(funnelId: string) {
  return useQuery({
    queryKey: ['funnel-metrics', funnelId],
    queryFn: async () => {
      const response = await fetch(
        `/functions/v1/analytics-funnel-analysis/funnels/${funnelId}/metrics`,
      );

      if (!response.ok) {
        throw new Error('Failed to fetch funnel metrics');
      }

      return response.json();
    },
    enabled: !!funnelId,
  });
}

/**
 * Hook: Create a new funnel
 */
export function useCreateFunnel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      name: string;
      description?: string;
      stages: string[];
      start_date?: string;
      end_date?: string;
    }) => {
      const response = await fetch('/functions/v1/analytics-funnel-analysis/funnels', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        const err = (await response.json()) as { error?: { message?: string } };
        throw new Error(err.error?.message || 'Failed to create funnel');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['funnels'] });
    },
  });
}

/**
 * Hook: Log a funnel event
 */
export function useLogFunnelEvent(funnelId: string) {
  return useMutation({
    mutationFn: async (input: {
      entity_type: 'user' | 'company';
      entity_id: string;
      stage: string;
      metadata?: Record<string, any>;
    }) => {
      const response = await fetch(
        `/functions/v1/analytics-funnel-analysis/funnels/${funnelId}/event`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input),
        },
      );

      if (!response.ok) {
        const err = (await response.json()) as { error?: { message?: string } };
        throw new Error(err.error?.message || 'Failed to log event');
      }

      return response.json();
    },
  });
}

/**
 * Hook: Fetch predictive scores of a given type
 */
export function usePredictiveScores(
  scoreType: 'churn_risk' | 'upsell_opportunity' | 'hiring_forecast',
  limit = 20,
) {
  return useQuery({
    queryKey: ['predictive-scores', scoreType, limit],
    queryFn: async () => {
      const response = await fetch(
        `/functions/v1/analytics-predictive-scores/scores?type=${scoreType}&limit=${limit}`,
      );

      if (!response.ok) {
        throw new Error('Failed to fetch predictive scores');
      }

      return response.json();
    },
  });
}

/**
 * Hook: Compute a predictive score on-demand
 */
export function useComputePredictiveScore() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      score_type: 'churn_risk' | 'upsell_opportunity' | 'hiring_forecast';
      entity_type: 'user' | 'company';
      entity_id: string;
    }) => {
      const response = await fetch('/functions/v1/analytics-predictive-scores/scores/compute', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        const err = (await response.json()) as { error?: { message?: string } };
        throw new Error(err.error?.message || 'Failed to compute score');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['predictive-scores'] });
    },
  });
}

/**
 * Hook: Fetch a specific company's churn risk score
 */
export function useChurnRiskScore(companyId: string) {
  return useQuery({
    queryKey: ['churn-risk-score', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('predictive_scores')
        .select('*')
        .eq('score_type', 'churn_risk')
        .eq('entity_type', 'company')
        .eq('entity_id', companyId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data || null;
    },
    enabled: !!companyId,
  });
}

/**
 * Hook: Fetch a specific company's upsell opportunity score
 */
export function useUpsellOpportunityScore(companyId: string) {
  return useQuery({
    queryKey: ['upsell-opportunity-score', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('predictive_scores')
        .select('*')
        .eq('score_type', 'upsell_opportunity')
        .eq('entity_type', 'company')
        .eq('entity_id', companyId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data || null;
    },
    enabled: !!companyId,
  });
}

/**
 * Hook: Fetch a specific company's hiring forecast score
 */
export function useHiringForecastScore(companyId: string) {
  return useQuery({
    queryKey: ['hiring-forecast-score', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('predictive_scores')
        .select('*')
        .eq('score_type', 'hiring_forecast')
        .eq('entity_type', 'company')
        .eq('entity_id', companyId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data || null;
    },
    enabled: !!companyId,
  });
}
